import env from '@config/env';
import CryptoJS from 'crypto-js';

/**
 * AES/CBC/PKCS7 encryption compatible with Java
 * com.dine.savvy.common.util.EncryptDecryptUtils.
 *
 * Key/IV derivation: MD5(keyMaterial) as lowercase hex, split in half.
 * - Logged out: keyMaterial = ENCRYPTION_KEY (SECRET_KEY)
 * - Logged in: keyMaterial = ENCRYPTION_KEY + userId
 * Output: Base64(ciphertext) — no IV or tag on the wire.
 */
const DEFAULT_SECRET_KEY = 'Savvy.Application';

const getSecretKey = () => env.ENCRYPTION_KEY || DEFAULT_SECRET_KEY;

/**
 * Returns [aesKeyHexSubstring, ivHexSubstring] — each 16 hex chars as UTF-8 key material.
 */
const deriveKeyAndIv = (userId) => {
  let keyMaterial = getSecretKey();
  if (userId != null && userId !== '') {
    keyMaterial = `${keyMaterial}${userId}`;
  }

  const hex = CryptoJS.MD5(CryptoJS.enc.Utf8.parse(keyMaterial)).toString(CryptoJS.enc.Hex);
  const mid = Math.floor(hex.length / 2);
  return [hex.substring(0, mid), hex.substring(mid)];
};

/**
 * Encrypts [value] using AES/CBC/PKCS7.
 *
 * @param {string} value Plaintext
 * @param {string|null|undefined} userId User UUID when logged in; omit/null for pre-login
 * @returns {string|null} Base64 ciphertext, or null on failure
 */
export const encrypt = (value, userId) => {
  try {
    if (value == null) return null;

    const [keyHex, ivHex] = deriveKeyAndIv(userId);
    const key = CryptoJS.enc.Utf8.parse(keyHex);
    const iv = CryptoJS.enc.Utf8.parse(ivHex);

    const encrypted = CryptoJS.AES.encrypt(String(value), key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });

    return encrypted.ciphertext.toString(CryptoJS.enc.Base64);
  } catch (error) {
    if (env.IS_DEV) {
      console.error('Unable to encrypt:', error);
    }
    return null;
  }
};

/**
 * Decrypts a Base64 ciphertext produced by [encrypt] or the Java/Flutter utility.
 *
 * @param {string} encrypted Base64 ciphertext
 * @param {string|null|undefined} userId User UUID used at encrypt time
 * @returns {string|null} Plaintext, or null on failure
 */
export const decrypt = (encrypted, userId) => {
  try {
    if (!encrypted) return null;

    const [keyHex, ivHex] = deriveKeyAndIv(userId);
    const key = CryptoJS.enc.Utf8.parse(keyHex);
    const iv = CryptoJS.enc.Utf8.parse(ivHex);

    const decrypted = CryptoJS.AES.decrypt(
      { ciphertext: CryptoJS.enc.Base64.parse(String(encrypted)) },
      key,
      {
        iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7,
      },
    );

    const plain = decrypted.toString(CryptoJS.enc.Utf8);
    return plain || null;
  } catch (error) {
    if (env.IS_DEV) {
      console.error('Unable to decrypt:', error);
    }
    return null;
  }
};

export const encryptDecryptUtils = {
  encrypt,
  decrypt,
  deriveKeyAndIv,
};

export default encryptDecryptUtils;
